/**
 * BudgetCategoryTable - Displays budget categories with planned amounts
 * Supports editing, adding, and removing categories.
 * Mobile-responsive: uses cards on small screens, table on larger screens.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BudgetCategory, Category } from '@budgira/shared';
import { cn } from '../../utils/cn';

interface BudgetCategoryTableProps {
  categories: BudgetCategory[];
  type: 'debit' | 'credit';
  total: number;
  title: string;
  editable?: boolean;
  availableCategories?: Category[];
  onAmountChange?: (categoryId: string, amount: number) => void;
  onNotesChange?: (categoryId: string, notes: string) => void;
  onAddCategory?: (category: Category) => void;
  onRemoveCategory?: (categoryId: string) => void;
}

export const BudgetCategoryTable = ({
  categories,
  type,
  total,
  title,
  editable = false,
  availableCategories = [],
  onAmountChange,
  onNotesChange,
  onAddCategory,
  onRemoveCategory,
}: BudgetCategoryTableProps) => {
  const { t, i18n } = useTranslation();
  const [showAddModal, setShowAddModal] = useState(false);

  const filteredCategories = categories.filter((c) => c.baseType === type);

  // Create a map of category logos from available categories
  const categoryLogoMap = new Map(
    availableCategories.map((c) => [c.id, c.logoUrl])
  );

  // Get categories that can be added (not already in the budget)
  const existingCategoryIds = new Set(
    filteredCategories.map((c) => c.categoryId).filter(Boolean)
  );
  const addableCategories = availableCategories.filter(
    (c) => c.baseType === type && !existingCategoryIds.has(c.id)
  );

  const formatAmount = (amount: number) => {
    return amount.toLocaleString(i18n.language === 'fr' ? 'fr-FR' : 'en-US', {
      style: 'currency',
      currency: 'EUR',
    });
  };

  const isExpense = type === 'debit';
  const colorClass = isExpense
    ? 'text-red-600 dark:text-red-400'
    : 'text-green-600 dark:text-green-400';
  const bgClass = isExpense
    ? 'bg-red-50 dark:bg-red-900/20'
    : 'bg-green-50 dark:bg-green-900/20';
  const borderClass = isExpense
    ? 'border-red-200 dark:border-red-800'
    : 'border-green-200 dark:border-green-800';

  return (
    <div className={cn('rounded-xl border', borderClass, bgClass)}>
      {/* Header */}
      <div className="p-3 sm:p-4 border-b border-slate-200/50 dark:border-slate-700/50 flex items-center justify-between gap-2">
        <h3 className={cn('text-base sm:text-lg font-semibold', colorClass)}>{title}</h3>
        {editable && addableCategories.length > 0 && (
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-1 px-2 sm:px-3 py-1.5 text-xs sm:text-sm font-medium text-violet-600 dark:text-violet-400 hover:bg-violet-100 dark:hover:bg-violet-900/30 rounded-lg transition-colors whitespace-nowrap"
          >
            <PlusIcon className="w-4 h-4" />
            <span className="hidden xs:inline">{t('budgets.addCategory')}</span>
            <span className="xs:hidden">{t('common.add')}</span>
          </button>
        )}
      </div>

      {/* Content */}
      <div>
        {filteredCategories.length === 0 ? (
          <div className="p-6 sm:p-8 text-center text-slate-500 dark:text-slate-400">
            <p className="text-sm sm:text-base">{t('budgets.noCategoriesYet')}</p>
            {editable && addableCategories.length > 0 && (
              <button
                onClick={() => setShowAddModal(true)}
                className="mt-2 text-sm text-violet-600 dark:text-violet-400 hover:underline"
              >
                {t('budgets.addFirstCategory')}
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Mobile: Card layout */}
            <div className="sm:hidden divide-y divide-slate-200/50 dark:divide-slate-700/50">
              {filteredCategories.map((category) => (
                <div
                  key={category.id}
                  className="p-3 hover:bg-white/50 dark:hover:bg-slate-800/50 transition-colors"
                >
                  {/* Category name and delete button */}
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <span className="text-base flex-shrink-0">
                        {categoryLogoMap.get(category.categoryId || '') || '📁'}
                      </span>
                      <span className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">
                        {category.categoryName}
                      </span>
                    </div>
                    {editable && (
                      <button
                        onClick={() => onRemoveCategory?.(category.categoryId || category.id)}
                        className="p-1.5 text-slate-400 hover:text-red-600 dark:hover:text-red-400 transition-colors flex-shrink-0"
                        title={t('budgets.removeCategory')}
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  
                  {/* Amount */}
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs text-slate-500 dark:text-slate-400">
                      {t('budgets.plannedAmount')}
                    </span>
                    {editable ? (
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={category.plannedAmount}
                        onChange={(e) =>
                          onAmountChange?.(
                            category.categoryId || category.id,
                            parseFloat(e.target.value) || 0
                          )
                        }
                        className="w-28 px-2 py-1 text-right text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                      />
                    ) : (
                      <span className={cn('text-sm font-medium', colorClass)}>
                        {formatAmount(category.plannedAmount)}
                      </span>
                    )}
                  </div>
                  
                  {/* Notes (only in edit mode) */}
                  {editable && (
                    <div className="mt-2">
                      <input
                        type="text"
                        value={category.notes || ''}
                        onChange={(e) =>
                          onNotesChange?.(category.categoryId || category.id, e.target.value)
                        }
                        placeholder={t('budgets.notes') + '...'}
                        className="w-full px-2 py-1 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Desktop: Table layout */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-sm text-slate-500 dark:text-slate-400">
                    <th className="px-4 py-3 text-left font-medium">{t('budgets.category')}</th>
                    <th className="px-4 py-3 text-right font-medium">{t('budgets.plannedAmount')}</th>
                    {editable && <th className="px-4 py-3 text-left font-medium">{t('budgets.notes')}</th>}
                    {editable && <th className="px-4 py-3 w-10"></th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200/50 dark:divide-slate-700/50">
                  {filteredCategories.map((category) => (
                    <tr
                      key={category.id}
                      className="hover:bg-white/50 dark:hover:bg-slate-800/50 transition-colors"
                    >
                      <td className="px-4 py-3 text-slate-900 dark:text-slate-100">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">
                            {categoryLogoMap.get(category.categoryId || '') || '📁'}
                          </span>
                          <span>{category.categoryName}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {editable ? (
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={category.plannedAmount}
                            onChange={(e) =>
                              onAmountChange?.(
                                category.categoryId || category.id,
                                parseFloat(e.target.value) || 0
                              )
                            }
                            className="w-32 px-2 py-1 text-right text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                          />
                        ) : (
                          <span className={cn('font-medium', colorClass)}>
                            {formatAmount(category.plannedAmount)}
                          </span>
                        )}
                      </td>
                      {editable && (
                        <td className="px-4 py-3">
                          <input
                            type="text"
                            value={category.notes || ''}
                            onChange={(e) =>
                              onNotesChange?.(category.categoryId || category.id, e.target.value)
                            }
                            placeholder={t('budgets.notes') + '...'}
                            className="w-full px-2 py-1 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                          />
                        </td>
                      )}
                      {editable && (
                        <td className="px-4 py-3">
                          <button
                            onClick={() => onRemoveCategory?.(category.categoryId || category.id)}
                            className="p-1 text-slate-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                            title={t('budgets.removeCategory')}
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Total - always visible */}
      {filteredCategories.length > 0 && (
        <div className="p-3 sm:p-4 border-t border-slate-200/50 dark:border-slate-700/50 bg-white/50 dark:bg-slate-800/50">
          <div className="flex items-center justify-between">
            <span className="font-semibold text-slate-900 dark:text-slate-100">Total</span>
            <span className={cn('text-lg sm:text-xl font-bold', colorClass)}>
              {formatAmount(total)}
            </span>
          </div>
        </div>
      )}

      {/* Add Category Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowAddModal(false)}
          />
          <div className="relative bg-white dark:bg-slate-800 rounded-xl shadow-xl p-4 sm:p-6 max-w-md w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-slate-100">
                {t('budgets.addCategory')}
              </h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
              >
                <XIcon className="w-5 h-5" />
              </button>
            </div>

            {addableCategories.length === 0 ? (
              <p className="text-center py-8 text-sm text-slate-500 dark:text-slate-400">
                {t('budgets.allCategoriesAdded')}
              </p>
            ) : (
              <div className="overflow-y-auto flex-1 -mx-4 sm:-mx-6 px-4 sm:px-6 divide-y divide-slate-200 dark:divide-slate-700">
                {addableCategories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => {
                      onAddCategory?.(cat);
                      setShowAddModal(false);
                    }}
                    className="w-full flex items-center gap-3 px-2 py-3 text-left hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
                  >
                    <span className="w-8 h-8 flex items-center justify-center text-xl flex-shrink-0">
                      {cat.logoUrl || '📁'}
                    </span>
                    <span className="text-sm sm:text-base text-slate-900 dark:text-slate-100 truncate">
                      {cat.name}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Icons
const PlusIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  </svg>
);

const TrashIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
    />
  </svg>
);

const XIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M6 18L18 6M6 6l12 12"
    />
  </svg>
);

export default BudgetCategoryTable;
